import "@ids/style-button";
import "@ids/style-css-helpers";
import "@ids/style-error-message";
import "@ids/style-input-field";
import "@ids/style-label";
import "@ids/style-loader";
import "@ids/style-modal-v19";
import "@ids/style-scaffolding";
import type { ComponentType } from "react";
import { createElement } from "react";
import { ProductType } from "@/models/ProductType";
import { getWebComponentLanguage } from "./hooks/locationLanguage";
import { injectGlobalStyles } from "./injectGlobalStyles";
import { prefetchEntryContent } from "./prefetchEntryContent";
import { prefetchEntryContext } from "./prefetchEntryContext";
import ProductShadowBase, {
  preloadWebComponentManifest,
} from "./productShadowBase";

injectGlobalStyles();
void preloadWebComponentManifest();

type EntryProps = Record<string, never>;

type EntryModule = {
  default: ComponentType<EntryProps>;
};

type EntryTagName =
  | "travel-entry"
  | "householder-entry"
  | "baby-entry"
  | "child-entry"
  | "dog-entry"
  | "cat-entry"
  | "van-entry"
  | "car-entry"
  | "mc-entry"
  | "landlord-entry"
  | "camper-entry"
  | "moped-entry"
  | "sport-entry"
  | "income-entry";

const entryProductByTagName: Record<EntryTagName, ProductType> = {
  "travel-entry": ProductType.TRAVEL,
  "householder-entry": ProductType.HOUSEHOLDER,
  "baby-entry": ProductType.BABY,
  "child-entry": ProductType.CHILD,
  "dog-entry": ProductType.DOG,
  "cat-entry": ProductType.CAT,
  "van-entry": ProductType.VAN,
  "car-entry": ProductType.CAR,
  "mc-entry": ProductType.MC,
  "landlord-entry": ProductType.LANDLORD,
  "camper-entry": ProductType.CAMPER,
  "moped-entry": ProductType.MOPED,
  "sport-entry": ProductType.SPORT,
  "income-entry": ProductType.INCOME,
};

function defineLazyEntryElement(
  tagName: EntryTagName,
  // Path of the dynamically-imported entry module, relative to
  // src/web_components/ and without extension (e.g. "travel/TravelEntryForm").
  // This MUST match the module specifier passed to `importEntry`'s import()
  // call below it, since it's used to derive the manifest key for CSS lookup
  // (see manifestEntrySource in productShadowBase.tsx). Keeping both values
  // next to each other at the call site (instead of an unrelated literal)
  // prevents them from drifting apart.
  modulePath: string,
  importEntry: () => Promise<EntryModule>,
) {
  class LazyEntryElement extends ProductShadowBase {
    connectedCallback() {
      const connectionVersion = this.beginConnection();
      this.manifestEntrySource = `src/web_components/${modulePath}.tsx`;
      this.primeResourceLoading();
      const language = getWebComponentLanguage(this.language ?? undefined);
      const product = entryProductByTagName[tagName];
      void importEntry()
        .then(({ default: Entry }) => {
          if (!this.isCurrentConnection(connectionVersion)) {
            return;
          }
          this.loadAndRender(() => createElement(Entry));
        })
        .catch((error: unknown) => {
          console.error(error);
        });

      prefetchEntryContent({
        product,
        language,
        backendHost: this.backendHost ?? undefined,
      });
      prefetchEntryContext({
        backendHost: this.backendHost ?? undefined,
      });
    }
  }

  customElements.define(tagName, LazyEntryElement);
}
defineLazyEntryElement(
  "travel-entry",
  "travel/TravelEntryForm",
  () => import("./travel/TravelEntryForm"),
);
defineLazyEntryElement(
  "householder-entry",
  "householder/HouseholderEntryForm",
  () => import("./householder/HouseholderEntryForm"),
);
defineLazyEntryElement(
  "baby-entry",
  "baby/BabyEntryForm",
  () => import("./baby/BabyEntryForm"),
);
defineLazyEntryElement(
  "child-entry",
  "child/ChildEntryForm",
  () => import("./child/ChildEntryForm"),
);
defineLazyEntryElement(
  "dog-entry",
  "dog/DogEntryForm",
  () => import("./dog/DogEntryForm"),
);
defineLazyEntryElement(
  "cat-entry",
  "cat/CatEntryForm",
  () => import("./cat/CatEntryForm"),
);
defineLazyEntryElement(
  "van-entry",
  "van/VanEntryForm",
  () => import("./van/VanEntryForm"),
);
defineLazyEntryElement(
  "car-entry",
  "car/CarEntryForm",
  () => import("./car/CarEntryForm"),
);
defineLazyEntryElement(
  "mc-entry",
  "mc/McEntryForm",
  () => import("./mc/McEntryForm"),
);
defineLazyEntryElement(
  "landlord-entry",
  "landlord/LandlordEntryForm",
  () => import("./landlord/LandlordEntryForm"),
);
defineLazyEntryElement(
  "camper-entry",
  "camper/CamperEntryForm",
  () => import("./camper/CamperEntryForm"),
);
defineLazyEntryElement(
  "moped-entry",
  "moped/MopedEntryForm",
  () => import("./moped/MopedEntryForm"),
);
defineLazyEntryElement(
  "sport-entry",
  "sport/SportEntryForm",
  () => import("./sport/SportEntryForm"),
);
defineLazyEntryElement(
  "income-entry",
  "income/IncomeEntryForm",
  () => import("./income/IncomeEntryForm"),
);
